import React, { useState, useEffect } from "react";
import { base44 } from "@voult/api/base44Client";
import { Receipt, Loader2, TrendingUp } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const STATUS_STYLES = {
  approved: "bg-emerald-100 text-emerald-700",
  declined: "bg-rose-100 text-rose-700",
  pending: "bg-yellow-100 text-yellow-700",
  refunded: "bg-blue-100 text-blue-700",
};

export default function PaymentHistory() {
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    base44.entities.PaymentTransaction.list("-created_date", 200).then(setTxns).finally(() => setLoading(false));
  }, []);

  const filtered = filter === "all" ? txns : txns.filter((t) => t.status === filter);
  const total = txns.filter((t) => t.status === "approved").reduce((s, t) => s + (t.amount || 0), 0);

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Payment History</h1>
          <p className="text-sm text-slate-500 mt-0.5">{txns.length} transaction{txns.length !== 1 ? "s" : ""}</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-slate-500">Total approved</p>
          <p className="text-xl font-semibold text-slate-900">${total.toFixed(2)}</p>
        </div>
      </div>

      <div className="flex gap-1 rounded-xl bg-slate-100 p-1 mb-5 w-fit">
        {["all", "approved", "pending", "declined", "refunded"].map((s) => (
          <button key={s} onClick={() => setFilter(s)}
            className={`rounded-lg px-3 py-1.5 text-xs font-medium capitalize transition ${filter === s ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-700"}`}>
            {s}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 text-slate-500">
          <Receipt className="h-10 w-10 mx-auto mb-3 text-slate-300" />
          <p>{filter === "all" ? "No transactions yet." : `No ${filter} transactions.`}</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100">
          {filtered.map((txn) => (
            <div key={txn.id} className="flex items-center gap-4 px-5 py-4">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900">{txn.merchant}</p>
                <p className="text-xs text-slate-500 mt-0.5">{txn.description || txn.reference || "—"}</p>
                {txn.masked_number && <p className="text-xs font-mono text-slate-400 mt-0.5">{txn.masked_number}</p>}
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-sm font-semibold text-slate-900">{txn.currency || "USD"} {(txn.amount || 0).toFixed(2)}</p>
                <span className={`text-[10px] font-semibold uppercase rounded-full px-2 py-0.5 ${STATUS_STYLES[txn.status] || "bg-slate-100 text-slate-600"}`}>{txn.status}</span>
                <p className="text-xs text-slate-400 mt-1">{txn.created_date ? formatDistanceToNow(new Date(txn.created_date), { addSuffix: true }) : ""}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}